import React from 'react';
import { motion } from 'motion/react';
import { 
  Sparkles, 
  Calendar, 
  Type, 
  Target, 
  Clock, 
  Lightbulb, 
  RefreshCw, 
  ArrowRight
} from 'lucide-react';
import SEO from '../components/SEO';
import { Link } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';

const Features = () => {
  const { t } = useTranslation();
  const features = [
    {
      icon: Sparkles,
      title: t.aiPostGen,
      desc: t.aiPostGenDesc,
      color: "bg-purple-100 text-purple-600"
    },
    {
      icon: Calendar,
      title: t.smartCalendar,
      desc: t.smartCalendarDesc,
      color: "bg-blue-100 text-blue-600"
    },
    {
      icon: Type,
      title: t.aiCaptionWriting,
      desc: t.aiCaptionWritingDesc,
      color: "bg-green-100 text-green-600"
    },
    {
      icon: Target,
      title: t.brandVoice,
      desc: t.brandVoiceDesc,
      color: "bg-orange-100 text-orange-600"
    },
    {
      icon: Clock,
      title: t.smartScheduling,
      desc: t.smartSchedulingDesc,
      color: "bg-red-100 text-red-600"
    },
    {
      icon: Lightbulb,
      title: t.endlessContentIdeas,
      desc: t.endlessContentIdeasDesc,
      color: "bg-yellow-100 text-yellow-600"
    }
  ];

  return (
    <div className="pt-24 pb-24 bg-[#030303] min-h-screen">
      <SEO 
        title={`${t.features} - PlataPost`}
        description={t.featuresHeroSubtitle2}
      />

      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-24 space-y-4">
          <p className="text-indigo-500 font-bold uppercase tracking-widest text-sm">
            {t.plataPostFeatures}
          </p>
          <h1 className="text-5xl lg:text-7xl font-black text-white leading-tight tracking-tighter">
            {t.builtForModernGrowth.split(' ').slice(0, -1).join(' ')} <span className="text-indigo-500">{t.builtForModernGrowth.split(' ').slice(-1)}</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto font-medium">
            {t.featuresHeroSubtitle2}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-32">
          {features.map((feature, i) => (
            <motion.div 
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              viewport={{ once: true }}
              className="bento-card group"
            >
              <div className={`w-20 h-20 bg-white/5 rounded-3xl flex items-center justify-center mb-8 border border-white/5 group-hover:scale-110 transition-transform`}>
                <feature.icon className="w-10 h-10 text-indigo-400" />
              </div>
              <h3 className="text-2xl font-black text-white mb-4 tracking-tight">{feature.title}</h3>
              <p className="text-gray-400 leading-relaxed font-medium">
                {feature.desc}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Deep Dive Section */}
        <div className="glass-card rounded-[3.5rem] p-12 lg:p-24 overflow-hidden relative group">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-indigo-600/10 blur-[120px] -mr-64 -mt-64" />
          
          <div className="grid lg:grid-cols-2 gap-20 items-center relative z-10">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-500/10 text-indigo-400 text-xs font-black uppercase tracking-widest border border-indigo-500/20">
                <RefreshCw className="w-4 h-4" />
                <span>{t.oneIdeaManyPlatforms}</span>
              </div>
              <h2 className="text-[42px] lg:text-5xl font-black text-white leading-tight tracking-tighter">
                {t.repurposeEffortlessly.split(' ').slice(0, -1).join(' ')} <br /> {t.repurposeEffortlessly.split(' ').slice(-1)}.
              </h2>
              <p className="text-gray-400 text-xl leading-relaxed max-w-xl font-medium">
                {t.repurposeEffortlesslyDesc}
              </p>
              <ul className="space-y-5 mb-10">
                {t.featureList.map((item: string) => (
                  <li key={item} className="flex items-center gap-4 text-gray-300 font-bold">
                    <div className="w-8 h-8 rounded-xl bg-indigo-500/10 text-indigo-400 flex items-center justify-center border border-indigo-500/20">
                      <Sparkles className="w-5 h-5" />
                    </div>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <Link 
                to="/dashboard" 
                className="inline-flex items-center gap-3 px-8 py-4 bg-indigo-600 text-white rounded-2xl font-black hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-600/20 group/btn"
              >
                <span>{t.startBuildingCalendar}</span>
                <ArrowRight className="w-5 h-5 group-hover/btn:translate-x-1 transition-transform" />
              </Link>
            </div>
            <div className="relative">
               <div className="aspect-square bg-indigo-600/20 rounded-[3rem] opacity-50 blur-[100px] absolute -inset-4" />
               <div className="relative glass-card border-white/10 rounded-[3rem] p-10">
                  <div className="space-y-8">
                    <div className="h-6 w-1/2 bg-white/10 rounded-full animate-pulse" />
                    <div className="h-6 w-full bg-white/10 rounded-full animate-pulse delay-75" />
                    <div className="h-6 w-3/4 bg-white/10 rounded-full animate-pulse delay-150" />
                    <div className="grid grid-cols-2 gap-6 mt-12">
                      <div className="h-40 bg-indigo-500/10 rounded-3xl border border-indigo-500/20 flex items-center justify-center">
                         <Sparkles className="w-10 h-10 text-indigo-400/50" />
                      </div>
                      <div className="h-40 bg-purple-500/10 rounded-3xl border border-purple-500/20" />
                    </div>
                  </div>
               </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Features;
